/**
 * Transactions Page - Notion-Inspired Design
 * View and filter all loyalty program transactions
 */

import React, { useEffect, useState } from 'react';
import { transactionsApi, Transaction } from '../lib/api';
import { toast } from '../components/ui';
import { Button, Input, Card, Badge, TableContainer, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TablePagination, TableSkeleton, EmptyState, NoTransactionsIllustration } from '../components/ui';

type DateRangeFilter = '7d' | '30d' | '90d' | 'all' | 'custom';

export const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'EARN' | 'REDEEM' | 'BONUS'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'CONFIRMED' | 'FAILED'>('ALL');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('30d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadTransactions();
  }, [typeFilter, statusFilter, dateRangeFilter, customStartDate, customEndDate, page]);

  const getDateRangeParams = () => {
    const now = new Date();
    let startDate: Date | null = null;

    switch (dateRangeFilter) {
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            startDate: customStartDate,
            endDate: customEndDate,
          };
        }
        return {};
      case 'all':
      default:
        return {};
    }

    if (startDate) {
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0],
      };
    }

    return {};
  };

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const filters: Record<string, string | number> = { page, limit };
      if (typeFilter !== 'ALL') filters.type = typeFilter;
      if (statusFilter !== 'ALL') filters.status = statusFilter;

      const dateRange = getDateRangeParams();
      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;

      const response = await transactionsApi.getAll(filters);
      setTransactions(response.data.data);
      setTotal(response.data.meta.total);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      toast.error('Failed to load transactions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeBadgeVariant = (type: Transaction['type']): 'success' | 'warning' | 'info' | 'default' => {
    switch (type) {
      case 'EARN':
        return 'success';
      case 'REDEEM':
        return 'warning';
      case 'BONUS':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusBadgeVariant = (status: Transaction['status']): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'CONFIRMED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleExportCSV = async () => {
    try {
      const dateRange = getDateRangeParams();
      const filters: { startDate?: string; endDate?: string; type?: string } = {};
      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;
      if (typeFilter !== 'ALL') filters.type = typeFilter;
      const response = await transactionsApi.export(filters);
      const url = URL.createObjectURL(new Blob([response.data as BlobPart], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export transactions:', error);
      toast.error('Failed to export transactions. Please try again.');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const earnedTotal = transactions
    .filter((t) => t.type === 'EARN' || t.type === 'BONUS')
    .reduce((sum, t) => sum + (parseFloat(t.b3trAmount?.toString() || '0')), 0);

  const redeemedTotal = transactions
    .filter((t) => t.type === 'REDEEM')
    .reduce((sum, t) => sum + (parseFloat(t.b3trAmount?.toString() || '0')), 0);

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">
            Transactions
          </h1>
          <p className="text-base mt-2 text-black-lighter">
            {total} total transactions across your loyalty program
          </p>
        </div>
        <Button variant="secondary" size="md" onClick={handleExportCSV} className="whitespace-nowrap self-start sm:self-auto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-blue-100 text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Total Transactions
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {(total || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-success-100 text-success-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Tokens Distributed
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {(earnedTotal || 0).toLocaleString()} Stars
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-warning-100 text-warning-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Tokens Redeemed
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {(redeemedTotal || 0).toLocaleString()} Stars
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-5">
          <div className="flex flex-col gap-4">
            {/* Search & Type/Status Filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Search by customer or transaction ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Input
                type="select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
                className="md:min-w-37.5"
              >
                <option value="ALL">All Types</option>
                <option value="EARN">Earned</option>
                <option value="REDEEM">Redeemed</option>
                <option value="BONUS">Bonus</option>
              </Input>
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="md:min-w-37.5"
              >
                <option value="ALL">All Status</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </Input>
            </div>

            {/* Date Range Filter */}
            <div className="pt-3 border-t border-border-light">
              <label className="block text-xs font-semibold text-black mb-2">Date Range</label>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Quick Date Range Pills */}
                <div className="flex flex-wrap gap-2">
                  {(['7d', '30d', '90d', 'all', 'custom'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setDateRangeFilter(range)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                        dateRangeFilter === range
                          ? 'bg-orange-400 text-white'
                          : 'bg-cream-100 text-black-lighter hover:bg-cream-200'
                      }`}
                    >
                      {range === '7d' && '7 Days'}
                      {range === '30d' && '30 Days'}
                      {range === '90d' && '90 Days'}
                      {range === 'all' && 'All Time'}
                      {range === 'custom' && '📅 Custom'}
                    </button>
                  ))}
                </div>

                {/* Custom Date Inputs */}
                {dateRangeFilter === 'custom' && (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="min-w-35"
                      placeholder="Start date"
                    />
                    <span className="text-black-lighter">to</span>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="min-w-35"
                      placeholder="End date"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Active Filters Summary */}
            {(typeFilter !== 'ALL' || statusFilter !== 'ALL' || dateRangeFilter !== 'all') && (
              <div className="flex items-center gap-2 pt-2 border-t border-border-light">
                <span className="text-xs font-semibold text-black-lighter">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {typeFilter !== 'ALL' && (
                    <Badge variant="default">
                      Type: {typeFilter}
                      <button
                        onClick={() => setTypeFilter('ALL')}
                        className="ml-1.5 hover:text-black"
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {statusFilter !== 'ALL' && (
                    <Badge variant="default">
                      Status: {statusFilter}
                      <button
                        onClick={() => setStatusFilter('ALL')}
                        className="ml-1.5 hover:text-black"
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {dateRangeFilter !== 'all' && (
                    <Badge variant="default">
                      {dateRangeFilter === 'custom'
                        ? `${customStartDate || '...'} to ${customEndDate || '...'}`
                        : dateRangeFilter.toUpperCase()}
                      <button
                        onClick={() => {
                          setDateRangeFilter('all');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className="ml-1.5 hover:text-black"
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  <button
                    onClick={() => {
                      setTypeFilter('ALL');
                      setStatusFilter('ALL');
                      setDateRangeFilter('all');
                      setCustomStartDate('');
                      setCustomEndDate('');
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Transactions Table */}
      {isLoading ? (
        <Card>
          <div className="p-5">
            <TableSkeleton rows={10} columns={6} />
          </div>
        </Card>
      ) : transactions.length === 0 ? (
        <EmptyState
          illustration={<NoTransactionsIllustration />}
          title="No transactions yet"
          description="Transactions will appear here as customers earn and redeem tokens"
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Date & Time</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    <p className="text-base text-black">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-black-lighter">
                      {new Date(transaction.createdAt).toLocaleTimeString()}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-base font-medium text-black">
                      {transaction.customerId.slice(0, 8)}...
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getTypeBadgeVariant(transaction.type)}>
                      {transaction.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="text-base font-medium text-black">
                      {transaction.type === 'REDEEM' ? '-' : '+'}
                      {parseFloat(transaction.b3trAmount?.toString() || '0').toLocaleString()} Stars
                    </p>
                    {transaction.fiatAmount && (
                      <p className="text-xs text-black-lighter">
                        ${parseFloat(transaction.fiatAmount.toString()).toFixed(2)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(transaction.status)}>
                      {transaction.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="tertiary" size="sm">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={total}
              itemsPerPage={limit}
            />
          )}
        </TableContainer>
      )}
    </div>
  );
};
